import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { DashboardSaleSummary } from "../../../types/dashboard.type";
import {
  getDashboardSaleSummary,
  getDashboardWasteBreakdown,
  getDashboardLeftOverBreakdown,
} from "../../../services";
import { formatNumber, numberWithCommas, numericFontSize } from "../../../utils";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import ChangeBadge from "./ChangeBadge";
import NoteCaptions from "../NoteCaptions";
import { COMPACT_QUERY, useCompact } from "../../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

interface OverviewCardsProps {
  /** Branch scope (สาขา). Drives the fetch but NOT the period — always today. */
  branchCode: string;
}

interface OverviewCard {
  key: string;
  title: string;
  value: string;
  /** Sale-target card is highlighted and shows the target icon (no badge). */
  highlight?: boolean;
  /** The ใบลดหนี้ / ใบเพิ่มหนี้ already netted into `value`, drawn under the title. */
  creditNoteAmount?: number;
  debitNoteAmount?: number;
  badge?: { percent: number; label: string; tone?: "success" | "error" | "warning" };
}

// 6 cards across on a normal screen; the grid wraps gracefully when narrower.
const CARD_SIZE = { xs: 6, sm: 4, md: 2 };

const CARD_VALUE_FONT = {
  base: "1.35rem",
  mid: "1.1rem",
  small: "0.95rem",
  tiny: "0.8rem",
};

/** Fixed comparison label — this section is always "today". */
const PREV_LABEL = "จากเมื่อวาน";

/**
 * "ภาพรวมวันนี้" — a TODAY-ONLY summary. Deliberately self-contained (like
 * BranchSalesChart): it fetches its own today-scoped data and is NOT bound to
 * the รายวัน/รายเดือน/รายปี period selector. Only the branch (สาขา) filter
 * scopes it.
 */
export default function OverviewCards({ branchCode }: OverviewCardsProps) {
  const isCompact = useCompact();

  // Anchor to "today" once; this section never follows the period selector.
  const today = useMemo(() => dayjs().format("YYYY-MM-DD"), []);

  const [summary, setSummary] = useState<DashboardSaleSummary>();
  const [leftoverQty, setLeftoverQty] = useState<number>(0);
  const [leftoverPercentDiff, setLeftoverPercentDiff] = useState<number>(0);
  const [wasteQty, setWasteQty] = useState<number>(0);
  const [wastePercentDiff, setWastePercentDiff] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  const onLoad = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [summaryRes, wasteRes, leftoverRes] = await Promise.all([
        getDashboardSaleSummary(today, today, branchCode),
        getDashboardWasteBreakdown(today, today, "day", branchCode),
        getDashboardLeftOverBreakdown(today, today, "day", branchCode),
      ]);

      setSummary(summaryRes.data.data);
      const waste = wasteRes.data.data;
      const leftover = leftoverRes.data.data;
      setWasteQty(waste?.current_waste_qty ?? 0);
      setWastePercentDiff(waste?.percent_waste_qty_diff ?? 0);
      setLeftoverQty(leftover?.current_leftover_qty ?? 0);
      setLeftoverPercentDiff(leftover?.percent_leftover_qty_diff ?? 0);
    } catch {
      setError(true);
      setSummary(undefined);
    } finally {
      setLoading(false);
    }
  }, [today, branchCode]);

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  // Not loading but no summary (e.g. API error) → show an error state instead
  // of spinning on the skeleton forever.
  if (!loading && (error || !summary)) {
    return (
      <Paper sx={{ height: "100%" }}>
        <Typography variant="body1" fontWeight={600} mb={2}>
          ภาพรวมวันนี้
        </Typography>
        <Box
          sx={{
            height: 132,
            [compact]: { height: 112 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color="error" fontWeight={500}>
            ไม่สามารถโหลดข้อมูลได้
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ height: "100%" }}>
      <Typography variant="body1" fontWeight={600} mb={2}>
        ภาพรวมวันนี้
      </Typography>

      <Grid container spacing={isCompact ? 1.5 : 2}>
        {loading || !summary
          ? Array.from({ length: 6 }).map((_, index) => (
              <Grid key={index} size={CARD_SIZE}>
                <Paper
                  variant="outlined"
                  sx={{
                    height: 116,
                    [compact]: { height: 100 },
                    boxShadow: "none",
                  }}
                >
                  <Skeleton variant="text" width="70%" height={22} />
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={32}
                    sx={{ mt: 2 }}
                  />
                </Paper>
              </Grid>
            ))
          : buildCards({
              summary,
              leftoverQty,
              leftoverPercentDiff,
              wasteQty,
              wastePercentDiff,
            }).map((card) => (
              <Grid key={card.key} size={CARD_SIZE}>
                <Paper
                  variant="outlined"
                  sx={{
                    height: 116,
                    [compact]: { height: 100 },
                    boxShadow: "none",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    minWidth: 0,
                    overflow: "hidden",
                    bgcolor: card.highlight
                      ? (theme) => theme.palette.primary.main + "12"
                      : "#fff",
                    borderColor: card.highlight
                      ? (theme) => theme.palette.primary.main + "55"
                      : undefined,
                  }}
                >
                  {/* Top row: title (+ target icon ONLY for the sale-target card) */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={1}
                  >
                    <Stack>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          color: (theme) => theme.palette.gray.main,
                          lineHeight: 1.25,
                        }}
                      >
                        {card.title}
                      </Typography>
                      <NoteCaptions
                        creditNoteAmount={card.creditNoteAmount}
                        debitNoteAmount={card.debitNoteAmount}
                        decimals={0}
                      />
                    </Stack>
                    {card.highlight && (
                      <Box
                        role="img"
                        aria-label="เป้ายอดขาย"
                        sx={{
                          width: 24,
                          height: 24,
                          flexShrink: 0,
                          bgcolor: "primary.main",
                          WebkitMaskImage: "url(/icons/target.png)",
                          maskImage: "url(/icons/target.png)",
                          WebkitMaskSize: "contain",
                          maskSize: "contain",
                          WebkitMaskRepeat: "no-repeat",
                          maskRepeat: "no-repeat",
                          WebkitMaskPosition: "center",
                          maskPosition: "center",
                        }}
                      />
                    )}
                  </Box>

                  {/* Bottom row: value + the badge GROUPED with its comparison
                      label (pill stacked over "จากเมื่อวาน"). The sale-target card
                      is excluded — it shows only its value. */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-end"
                    gap={1}
                    sx={{ minWidth: 0 }}
                  >
                    <Typography
                      fontWeight={700}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        color: card.highlight ? "primary.main" : "text.primary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: 1.2,
                        fontSize: numericFontSize(card.value, CARD_VALUE_FONT),
                      }}
                      title={card.value}
                    >
                      {card.value}
                    </Typography>
                    {!card.highlight && card.badge && (
                      <ChangeBadge
                        percent={card.badge.percent}
                        label={card.badge.label}
                        tone={card.badge.tone}
                      />
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
      </Grid>
    </Paper>
  );
}

function buildCards({
  summary,
  leftoverQty,
  leftoverPercentDiff,
  wasteQty,
  wastePercentDiff,
}: {
  summary: DashboardSaleSummary;
  leftoverQty: number;
  leftoverPercentDiff: number;
  wasteQty: number;
  wastePercentDiff: number;
}): OverviewCard[] {
  return [
    {
      key: "target",
      title: "เป้ายอดขาย",
      value: `฿${formatNumber(summary.sales_target, { decimals: 0 })}`,
      highlight: true,
    },
    {
      key: "net-sales",
      title: "ยอดขายสุทธิวันนี้",
      value: `฿${formatNumber(summary.total_sales_amount, { decimals: 0 })}`,
      creditNoteAmount: summary.credit_note_amount,
      debitNoteAmount: summary.debit_note_amount,
      badge: {
        percent: summary.percent_compare_previous_sales,
        label: PREV_LABEL,
      },
    },
    {
      key: "bill-count",
      title: "จำนวนบิล",
      value: `${numberWithCommas(summary.total_count)} ใบ`,
      badge: {
        percent: summary.percent_compare_previous_count,
        label: PREV_LABEL,
      },
    },
    {
      key: "selling-branch",
      title: "จำนวนสาขาที่ขาย",
      value: `${numberWithCommas(summary.selling_branch_count)} สาขา`,
      badge: {
        percent: summary.percent_compare_previous_selling_branch,
        label: PREV_LABEL,
      },
    },
    {
      key: "leftover",
      title: "จำนวนของเหลือ",
      value: `${numberWithCommas(leftoverQty)} ชิ้น`,
      badge: { percent: leftoverPercentDiff, label: PREV_LABEL, tone: "warning" },
    },
    {
      key: "waste",
      title: "จำนวนของเสีย",
      value: `${numberWithCommas(wasteQty)} ชิ้น`,
      badge: { percent: wastePercentDiff, label: PREV_LABEL, tone: "error" },
    },
  ];
}
